import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveBar } from '@nivo/bar';
import { Fetch, ageInMonths } from '../../utils/Utils.js';
import '../reproduction/NivoChartCard.css';

// Ordered age bands, not distinct identities — a single metric (calf count)
// read across a progression, so one hue (categorical slot 1, blue) rather
// than a color per bar.
const BAR_COLOR = '#2a78d6';

const CATEGORIES = [
  { min: 7,  max: 7,  name: 'seven_months' },
  { min: 8,  max: 8,  name: 'eight_months' },
  { min: 9,  max: 9,  name: 'nine_months' },
  { min: 10, max: 12, name: 'ten_twelve_months' },
];

const categoryForAge = (months) => {
  if (months === null) return null;
  const category = CATEGORIES.find(c => months >= c.min && months <= c.max);
  return category ? category.name : null;
};

const AgeCategoryChart = () => {
  const { t } = useTranslation();
  const [animals, setAnimals] = useState([]);

  useEffect(() => {
    Fetch('/api/herd/animals', 'GET', null, (data) => setAnimals(data || []));
  }, []);

  const totals = {};
  CATEGORIES.forEach(c => { totals[c.name] = 0; });
  animals.forEach((a) => {
    if (a.is_alive === false) return;
    const category = categoryForAge(ageInMonths(a.birth_date));
    if (category) totals[category]++;
  });

  const total = Object.values(totals).reduce((a, b) => a + b, 0);
  const bars = CATEGORIES.map(c => ({ category: c.name, label: t(c.name), count: totals[c.name] }));

  return (
    <div className="Chart NivoCard AgeCategoryChart">
      <h3>{t('age_category')}</h3>
      {total > 0 && <p className="nivo-card-subtitle">{t('chart_summary_age_range', { count: total })}</p>}

      {total === 0 ? (
        <p className="nivo-card-empty">{t('chart_summary_no_age_data')}</p>
      ) : (
        <div style={{ height: 260 }}>
          <ResponsiveBar
            data={bars}
            keys={['count']}
            indexBy="label"
            margin={{ top: 12, right: 16, bottom: 32, left: 40 }}
            valueScale={{ type: 'linear' }}
            padding={0.4}
            borderRadius={4}
            colors={BAR_COLOR}
            enableGridY={true}
            gridYValues={4}
            axisBottom={{ tickSize: 0, tickPadding: 8 }}
            axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 4 }}
            label={(d) => d.value}
            labelSkipHeight={16}
            labelTextColor="#ffffff"
            motionConfig="gentle"
            theme={{
              axis: { ticks: { text: { fill: '#898781', fontSize: 12 } } },
              grid: { line: { stroke: '#e1e0d9', strokeWidth: 1 } },
              labels: { text: { fontWeight: 700, fontSize: 12.5 } },
            }}
            tooltip={({ data }) => (
              <div className="nivo-tooltip">
                <span className="nivo-tooltip-swatch" style={{ background: BAR_COLOR }} />
                <strong>{data.label}</strong>: <span className="nivo-tooltip-value">{data.count}</span>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default AgeCategoryChart;
